import React, { useMemo, useRef, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { MotifTooltip, MotifIcon } from '@ey-xd/motif-react';
import { actionIcInfoOutline24px } from '@ey-xd/motif-react/assets/icons';
import { AG_GRID_LICENSE_KEY } from '@constants/licenseKeys';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { columnDefsKeys, mslPastFooterColumnDefs } from './columnDefs';
import { columnDefsKeys2 } from './ColumnDefsInner';
import TableHeader from '@molecules/msl/components/forecast/tableHeader';
import { LicenseManager } from 'ag-grid-enterprise';
import '../../tableComponent/styles/style.css';
import { formateTOV } from '@util/commonUtil';
import {
  getPastForecastAccounts,
  getPasMSLrollingOpportunities,
  getPastMslGridData,
} from '@services/mslServices';
import { Current_User, Msl_Opportunity_State, Msl_Quarter_State_Info } from '@recoil/recoil';
import {
  MSLHeadTable,
  TooltipContainer,
  TooltipContent,
  TooltipStyle,
  TooltipIconWrapperDark,
} from '../mslTable.styles';
import { SOURCE, MSL_TOOLTIP_MESSAGES } from '@constants/commonConstants';
import { FooterTable } from '@atoms/msl/mslTable.styles';
import { appInsights } from '@services/appInsightsService';
import InnerTableHeader from '@molecules/msl/components/forecast/innerTableHeader';

LicenseManager.setLicenseKey(AG_GRID_LICENSE_KEY);
let gridApi; // eslint-disable-line
const MslPastTable = props => {
  const topGrid = useRef(null);
  const bottomGrid = useRef(null);
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);
  const [rowData, setRowData] = useState([]); // eslint-disable-line
  const [columnDefs, setColumnDefs] = useState(columnDefsKeys); // eslint-disable-line
  const currentUser = useRecoilValue(Current_User);
  const [mslRowFooterData, setMslRowFooterData] = useRecoilState(Msl_Opportunity_State);
  const [showNowRowsToShowText, setShowNowRowsToShowText] = useState(true); // eslint-disable-line
  const quarterInfo = useRecoilValue(Msl_Quarter_State_Info) || {};
  const [resetQuarterInfo, setResetQuarterInfo] = useRecoilState(Msl_Quarter_State_Info); // eslint-disable-line
  const [isLoading, setIsLoading] = useState(false); // eslint-disable-line
  const gridRef = useRef(null); // Reference to the grid

  const TYPE = props.forecast_type;
  const EMAIL = props.email;
  const MONTH = props?.month?.toLowerCase();
  const SUBMITTEDYEAR = props.year;
  const MSLID = props.account;
  const QUARTER = props?.quarter?.toLowerCase();
  const linkForAccountSegments = useRef({});

  useEffect(() => {
    const handleResize = () => {
      if (gridRef.current) {
        gridRef.current.api.sizeColumnsToFit();
      }
    };

    // Initial column resizing
    handleResize();

    // Resize event listener
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    return () => {
      setResetQuarterInfo({});
    };
  }, []);

  //useEffect to fetch api response of account segment and footer tables
  useEffect(() => {
    setRowData([]);
    setMslRowFooterData([]);
    const quarterKeys = Object.keys(quarterInfo || {});
    const SUBMITTEDMONTH = TYPE === 'quarter' ? quarterKeys?.[0]?.toLowerCase() : MONTH;
    const fetchData = async () => {
      if (quarterKeys?.length > 0) {
        try {
          const getOpportunitiesDetailsAPI = await getPasMSLrollingOpportunities(
            EMAIL,
            MSLID,
            SUBMITTEDMONTH,
            SUBMITTEDYEAR,
            TYPE,
            QUARTER,
          );

          setShowNowRowsToShowText(false);
          const newData = [...getOpportunitiesDetailsAPI];
          setRowData(newData);
        } catch (error) {
          appInsights.trackTrace({
            message: JSON.stringify({
              message: `Error Fetching First API data for Accounts Segments: ${error}  `,
              userId: localStorage.getItem('userEmail'),
              source: SOURCE.FRONTEND,
            }),
            severityLevel: 3, // 3 represents failure
          });
          setRowData([]);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchData();

    const fetchMslFooterData = async () => {
      if (quarterKeys?.length > 0) {
        try {
          const data = await getPastMslGridData(
            EMAIL,
            MSLID,
            SUBMITTEDMONTH,
            SUBMITTEDYEAR,
            TYPE,
            QUARTER,
          );

          if (data && data.length > 0) {
            const innerAGData = Object.keys(data[0]).map(key => ({
              category: data[0][key]?.category,
              assurance: data[0][key]?.assurance,
              consulting: data[0][key]?.consulting,
              sat: data[0][key]?.sat,
              tax: data[0][key]?.tax,
              itts: data[0][key]?.itts,
              emeia: data[0][key]?.emeia,
              total: data[0][key]?.total,
            }));

            setMslRowFooterData(innerAGData);
          }
        } catch (error) {
          appInsights.trackTrace({
            message: JSON.stringify({
              message: `Error Fetching Footer API data for Footer Table: ${error}  `,
              userId: localStorage.getItem('userEmail'),
              source: SOURCE.FRONTEND,
            }),
            severityLevel: 3, // 3 represents failure
          });
          setMslRowFooterData([]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchMslFooterData();
  }, [currentUser, quarterInfo]);

  //to get the grid ready on data load
  const handleGridReady = params => {
    ({ api: gridApi } = params); // eslint-disable-line
  };

  //to navigate to CRM page on click of accounts link
  const LinkRenderer = ({ value, segmentKey }) => {
    const handleClick = event => {
      event.preventDefault();

      if (!segmentKey) {
        appInsights.trackTrace({
          message: JSON.stringify({
            message: 'Segment key is undefined. Cannot fetch data for the segment.',
            userId: localStorage.getItem('userEmail'),
            source: SOURCE.FRONTEND,
          }),
          severityLevel: 3, // 3 represents failure
        });
        return;
      }

      const segmentData = linkForAccountSegments.current[segmentKey];

      if (segmentData) {
        const clickedAccount = segmentData.find(item => item.name === value);

        if (clickedAccount) {
          if (
            localStorage.getItem('isSuperUser') === 'true' &&
            localStorage.getItem('targetUserEmail') !== 'null'
          ) {
            // MSL new tab window operation in super user mode - use userEmail for operations
            localStorage.setItem('isMslNewTabOperation', 'true');
            const accountId = clickedAccount.accountid;
            const url = `${process.env.REACT_APP_OPPS_NAME_IFRAME_URL}/main.aspx?appid=${process.env.REACT_APP_OPP_NAME_URL_APP_ID}&forceUCI=1&pagetype=entityrecord&etn=account&id=${accountId}`;
            window.open(url, '_blank', 'noopener,noreferrer');
          } else {
            // Regular user operation - MSL always opens in new tab window
            // they use userEmail via headers in TokenInterceptor
            localStorage.setItem('isMslNewTabOperation', 'false');
            const accountId = clickedAccount.accountid;
            const url = `${process.env.REACT_APP_OPPS_NAME_IFRAME_URL}/main.aspx?appid=${process.env.REACT_APP_OPP_NAME_URL_APP_ID}&forceUCI=1&pagetype=entityrecord&etn=account&id=${accountId}`;
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        } else {
          appInsights.trackTrace({
            message: JSON.stringify({
              message: `No matching account found for the clicked account name: ${value}.`,
              userId: localStorage.getItem('userEmail'),
              source: SOURCE.FRONTEND,
            }),
            severityLevel: 3, // 3 re-presents failure
          });
        }
      } else {
        appInsights.trackTrace({
          message: JSON.stringify({
            message: `No data found for the segment: ${segmentKey}`,
            userId: localStorage.getItem('userEmail'),
            source: SOURCE.FRONTEND,
          }),
          severityLevel: 3, // 3 re-presents failure
        });
      }
    };

    return value ? (
      <button
        type="button"
        onClick={handleClick}
        style={{
          textDecoration: 'none',
          color: '#155cb4',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
        }}
      >
        {value}
      </button>
    ) : null;
  };

  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      enableColumnMenu: false, // Enable column menu for all columns
      flex: 1,
    };
  }, []);
  //calls the second api on expand of the account segment and controls the inner accounts
  const handleAccordionToggle = async params => {
    const accountSegmentName = params?.data?.forecasted_acc_seg || params?.data?.won_acc_seg;

    if (!accountSegmentName) {
      appInsights.trackTrace({
        message: JSON.stringify({
          message: 'Account segment name is undefined.',
          userId: localStorage.getItem('userEmail'),
          source: SOURCE.FRONTEND,
        }),
        severityLevel: 3, // 3 represents failure
      });
      return;
    }

    const detailData = await fetchSecondApiData(accountSegmentName);

    const formattedData = detailData.map(item => ({
      ...item,
      gcsp: item?.gcsp_name,
      emiaacr: item?.emeia_acr,
      name: item?.name,
      accountid: item?.accountid,
      status: item?.status,
      segmentKey: accountSegmentName, // Include the segmentKey in the row data
    }));

    linkForAccountSegments.current[accountSegmentName] = formattedData; // Store the data in linkForAccountSegments

    params.successCallback(formattedData); // Pass the formatted data to the inner grid
  };

  const defaultInnerColDef = useMemo(() => {
    return {
      sortable: true, // Enable sorting globally
      filter: true, // Enable filtering globally
      enableColumnMenu: false, // Disable the column menu globally
      flex: 1,
    };
  }, []);

  //detail cell renderer params for the inner grid
  const detailCellRendererParams = useMemo(() => {
    return {
      detailGridOptions: {
        masterDetail: false,
        pagination: true,
        defaultColDef: defaultInnerColDef,

        suppressAggFuncInHeader: true,
        paginationPageSize: 5,
        columnDefs: columnDefsKeys2,
        autoGroupColumnDef: {
          headerName: 'Accounts',
          tooltipValueGetter: params => {
            if (!params?.value) {
              return null;
            }
            return `${params.value}`;
          },
          cellStyle: { display: 'flex', justifyContent: 'flex-start' },
          filter: 'agTextColumnFilter',
          filterValueGetter: params => {
            // For grouped rows, return the group key or a placeholder
            if (params.node.group) {
              return params.node.key; // Use the group key for filtering grouped rows
            }
            // For non-grouped rows, return the account name
            return params.data?.name || ''; // Ensure it returns an empty string if name is undefined
          },
          suppressMovable: true,
          cellRenderer: 'agGroupCellRenderer',
          headerComponent: InnerTableHeader,
          headerComponentParams: {
            enableSorting: true,
          },

          cellRendererParams: params => {
            // Find the segmentKey dynamically based on the account name
            let segmentKey = null;

            if (
              linkForAccountSegments.current &&
              typeof linkForAccountSegments.current === 'object'
            ) {
              for (const key in linkForAccountSegments.current) {
                const segmentData = linkForAccountSegments.current[key];

                // Ensure segmentData is an array before calling .some()
                if (
                  Array.isArray(segmentData) &&
                  segmentData.some(item => item.name === params.value)
                ) {
                  segmentKey = key; // Set the segmentKey if the account name matches
                  break;
                }
              }
            }

            return {
              innerRenderer: LinkRenderer,
              suppressCount: true,
              accountid: params?.data?.accountid, // Pass the account ID
              segmentKey, // Pass the dynamically determined segmentKey
            };
          },
          minWidth: 170,
          hide: window.innerWidth < 600,
        },
        components: { agColumnHeader: InnerTableHeader },
      },
      getDetailRowData: handleAccordionToggle, // Use the new handler
    };
  }, [columnDefsKeys2, quarterInfo]);

  //cell renderer for the footer table
  const Past_Msl_CellRenderer = ({ value, data }) => {
    if (!isNaN(value) && value !== null) {
      // Truncate the decimal points and show the nearest integer value
      const truncatedValue = Math.floor(value);

      if (data.category === 'Accuracy(Your Total Forecasted vs Actual)') {
        return `${truncatedValue}%`;
      }
      return formateTOV(truncatedValue) || '$0';
    }
    return value || '$0';
  };

  // Category cell renderer with tooltip for specific category
  const msl_catagory_CellRenderer = ({ value }) => {
    // Add tooltip for Total Forecasted(PL Commit + Audit/TR + GCSP Judge)
    if (value === 'Total Forecasted(PL Commit + Audit/TR + GCSP Judge)') {
      return (
        <TooltipContainer>
          <span style={{ color: '#ffffff' }}>{value}</span>
          <MotifTooltip
            trigger={
              <TooltipIconWrapperDark>
                <MotifIcon src={actionIcInfoOutline24px} style={{ color: '#E6E6E6' }} />
              </TooltipIconWrapperDark>
            }
            allowHover={true}
            allowClick={true}
            placement="top"
            hideCloseButton={true}
            style={TooltipStyle}
          >
            <TooltipContent>
              {MSL_TOOLTIP_MESSAGES.TOTAL_FORECASTED_PL_COMMIT_AUDIT_TR_GCSP}
            </TooltipContent>
          </MotifTooltip>
        </TooltipContainer>
      );
    }

    return <span style={{ color: '#ffffff' }}>{value}</span>;
  };

  //style for the footer table
  const getFooterTableRowStyle = params => {
    if (
      params.data.category === 'Actual Sold(All Won)' ||
      params.data.category === 'Accuracy(Your Total Forecasted vs Actual)'
    ) {
      return { background: '#1A1A2A', color: '#ffffff', font: 'EYIntEYInterstate' };
    }
    return { background: '#3A3A4A', color: '#ffffff', font: 'EYIntEYInterstate' };
  };

  //fetches the second api of accounts data on expand of the account segment
  const fetchSecondApiData = async accountSegmentName => {
    const quarterKeys = Object.keys(quarterInfo || {});
    try {
      const SEGMENT = accountSegmentName;
      const NEXT = 500;
      const OFFSET = 0;
      const SUBMITTEDMONTH = TYPE === 'quarter' ? quarterKeys?.[0]?.toLowerCase() : MONTH;
      const getOpportunitiesDetailsAPI = await getPastForecastAccounts(
        MSLID,
        SUBMITTEDMONTH, // SUBMITTEDMONTH
        SUBMITTEDYEAR, // SUBMITTEDFISCALYEAR
        EMAIL,
        SEGMENT,
        TYPE,
        QUARTER,
        NEXT,
        OFFSET,
      );

      setShowNowRowsToShowText(false);
      const modifiedData = getOpportunitiesDetailsAPI.map(item => ({
        ...item,
        won_acc_seg: item?.won_acc_seg,
        accountid: item?.accountid, // Include accountid in the data
        segmentKey: SEGMENT, // Include segmentKey in the data
      }));

      linkForAccountSegments.current[SEGMENT] = modifiedData; // Store the data in linkForAccountSegments
      const updatedData = modifiedData?.filter(item => item?.value_type !== 'Account Totals'); // Exclude the objects with value_type 'Account Totals'
      updatedData?.sort((a, b) => (a?.value_type > b?.value_type ? -1 : 1)); // Sort data in descending order
      return updatedData;
    } catch (error) {
      return []; // Return an empty array in case of an error
    }
  };

  const isRowMaster = obj => {
    const isMaster = ['Core', 'Inbound'].includes(obj?.forecasted_acc_seg || obj?.won_acc_seg);
    return isMaster ? false : true;
  };

  //jsx element for the table
  return (
    <>
      <MSLHeadTable
        className="ag-theme-alpine "
        id="mslheadtable"
        style={{
          width: '100%',
          overflow: 'auto',
        }}
      >
        <div style={containerStyle}>
          <div style={gridStyle}>
            <AgGridReact
              ref={topGrid}
              alignedGrids={[bottomGrid]}
              defaultColDef={defaultColDef}
              tooltipShowDelay={0}
              enableBrowserTooltips={true}
              rowData={rowData}
              columnDefs={columnDefs}
              domLayout="autoHeight"
              masterDetail={true} // Enable master-detail functionality
              isRowMaster={isRowMaster}
              detailCellRendererParams={detailCellRendererParams}
              onGridReady={handleGridReady}
              components={{ agColumnHeader: TableHeader }}
            />
          </div>
        </div>
      </MSLHeadTable>
      <fragment style={{ width: '100vw', bottom: 0 }}>
        <FooterTable className="ag-theme-alpine" id="mslfootertable">
          <AgGridReact
            ref={bottomGrid}
            alignedGrids={[topGrid]}
            defaultColDef={defaultColDef}
            columnDefs={mslPastFooterColumnDefs}
            getRowStyle={getFooterTableRowStyle}
            rowData={mslRowFooterData}
            domLayout="autoHeight"
            components={{
              past_Msl_CellRenderer: Past_Msl_CellRenderer,
              msl_catagory_CellRenderer: msl_catagory_CellRenderer,
            }}
          />
        </FooterTable>
      </fragment>
    </>
  );
};

export default MslPastTable;
